import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Ban,
  CheckCircle2,
  Clock,
  Eye,
  Loader2,
  MapPinCheck,
  Play,
  Trash2,
  XCircle,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import {
  DataCard,
  DataCardActions,
  DataCardField,
  DataCardGrid,
  DataCardHeader,
  DataCardStatus,
  DataCardTitle,
} from "@/components/ui/data-card"
import { MobileFilterSheet } from "@/components/ui/mobile-filter-sheet"
import { MobileFilterSortBar } from "@/components/ui/mobile-filter-sort-bar"
import { MobileSortSheet, type SortOption } from "@/components/ui/mobile-sort-sheet"
import { useMobileFilterSort } from "@/hooks/use-mobile-filter-sort"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Select } from "@/components/ui/select"
import { consumer } from "@/lib/actioncable"

const SORT_OPTIONS: SortOption[] = [
  { sort: "created_at", direction: "desc", label: "Tanggal terbaru" },
  { sort: "created_at", direction: "asc", label: "Tanggal terlama" },
  { sort: "period", direction: "desc", label: "Periode terbaru" },
  { sort: "period", direction: "asc", label: "Periode terlama" },
  { sort: "outlet_count", direction: "desc", label: "Outlet terbanyak" },
  { sort: "outlet_count", direction: "asc", label: "Outlet paling sedikit" },
  { sort: "layer0_flagged_count", direction: "desc", label: "Bermasalah terbanyak" },
  { sort: "layer0_flagged_count", direction: "asc", label: "Bermasalah paling sedikit" },
  { sort: "status", direction: "asc", label: "Status A–Z" },
  { sort: "status", direction: "desc", label: "Status Z–A" },
]

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type RunStatus = "pending" | "processing" | "completed" | "failed" | "cancelled"
type RunScope = "layer0" | "layer0_pilot"

type RunRow = {
  id: number
  period_year: number
  period_month: number
  period_label: string
  scope: RunScope
  status: RunStatus
  outlet_count: number
  processed_count: number
  layer0_flagged_count: number
  pilot_outlet_count: number
  error_message: string | null
  layer1_note: string | null
  started_at: string | null
  finished_at: string | null
  created_at: string
  triggered_by: string | null
}

type PeriodOption = { value: string; label: string; outlet_count?: number }

type Filters = {
  status: string | null
  scope: string | null
  period: string | null
}

// OutletVerificationChannel messages. A deleted run's synthetic terminal
// message carries only run_id + status, so every other field is optional and
// merged defensively.
type StatusUpdate = {
  type: "status_update"
  run_id: number
  status: RunStatus
  outlet_count?: number
  processed_count?: number
  layer0_flagged_count?: number
  pilot_outlet_count?: number
  error_message?: string | null
  layer1_note?: string | null
  finished_at?: string | null
}

type ProgressUpdate = {
  type: "progress_update"
  run_id: number
  processed_count: number
  outlet_count: number
  // Layer-1 (pilot Google) progress messages are broadcast-only and carry a
  // phase marker; the counts then refer to the pilot subset, not the period.
  phase?: "layer1"
}

type Progress = { processed: number; total: number; phase?: "layer1" }

const TERMINAL_STATUSES: RunStatus[] = ["completed", "failed", "cancelled"]

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function AdminOutletVerificationRuns({
  runs,
  in_flight_runs,
  total,
  page,
  per_page,
  sort,
  direction,
  filters,
  filter_options,
  period_options,
  can_create,
  layer1_available,
  errors,
}: {
  runs: RunRow[]
  in_flight_runs: RunRow[]
  total: number
  page: number
  per_page: number
  sort: string
  direction: "asc" | "desc"
  filters: Filters
  filter_options: { statuses: string[]; scopes: string[]; periods: PeriodOption[] }
  period_options: PeriodOption[]
  can_create: boolean
  layer1_available: boolean
  errors?: Record<string, string>
}) {
  const [period, setPeriod] = React.useState(period_options[0]?.value ?? "")
  const [scopeChoice, setScopeChoice] = React.useState<RunScope>("layer0")
  const [creating, setCreating] = React.useState(false)
  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set())
  const [liveRuns, setLiveRuns] = React.useState<RunRow[]>(runs)
  const [liveInFlight, setLiveInFlight] = React.useState<RunRow[]>(in_flight_runs)
  const [progressMap, setProgressMap] = React.useState<Record<number, Progress>>({})

  React.useEffect(() => { setLiveRuns(runs) }, [runs])
  React.useEffect(() => { setLiveInFlight(in_flight_runs) }, [in_flight_runs])

  // Subscribe to the FULL active set (across all pages), sourced from the
  // in-flight panel. SSR-safe: ActionCable is only touched inside this effect.
  const liveInFlightKey = liveInFlight
    .map((r) => r.id)
    .sort((a, b) => a - b)
    .join(",")

  React.useEffect(() => {
    if (!liveInFlightKey) return
    const ids = liveInFlightKey.split(",").map((s) => parseInt(s, 10))

    function mergeRow(prev: RunRow[], data: StatusUpdate): RunRow[] {
      return prev.map((r) =>
        r.id === data.run_id
          ? {
              ...r,
              status: data.status,
              outlet_count: data.outlet_count ?? r.outlet_count,
              processed_count: data.processed_count ?? r.processed_count,
              layer0_flagged_count: data.layer0_flagged_count ?? r.layer0_flagged_count,
              pilot_outlet_count: data.pilot_outlet_count ?? r.pilot_outlet_count,
              error_message: data.error_message ?? r.error_message,
              layer1_note: data.layer1_note ?? r.layer1_note,
              finished_at: data.finished_at ?? r.finished_at,
            }
          : r,
      )
    }

    const subs = ids.map((id) =>
      consumer.subscriptions.create(
        { channel: "OutletVerificationChannel", run_id: id },
        {
          received(data: StatusUpdate | ProgressUpdate) {
            if (data.type === "progress_update") {
              setProgressMap((prev) => ({
                ...prev,
                [data.run_id]: {
                  processed: data.processed_count,
                  total: data.outlet_count,
                  phase: data.phase,
                },
              }))
              return
            }

            setLiveRuns((prev) => mergeRow(prev, data))

            if (TERMINAL_STATUSES.includes(data.status)) {
              setProgressMap((prev) => {
                const next = { ...prev }
                delete next[data.run_id]
                return next
              })
              setLiveInFlight((prev) => prev.filter((r) => r.id !== data.run_id))
              router.reload({ only: ["runs", "in_flight_runs", "filter_options", "can_create"] })
            } else {
              setLiveInFlight((prev) => mergeRow(prev, data))
            }
          },
        },
      ),
    )
    return () => subs.forEach((s) => s.unsubscribe())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveInFlightKey])

  // -------------------------------------------------------------------------
  // Navigation helper (URL-based filter/sort/page state)
  // -------------------------------------------------------------------------

  function navigate(overrides: Record<string, string | number | null>) {
    const params: Record<string, string | number> = {}
    if (filters.status)        params.status    = filters.status
    if (filters.scope)         params.scope     = filters.scope
    if (filters.period)        params.period    = filters.period
    if (sort !== "created_at") params.sort      = sort
    if (direction !== "desc")  params.direction = direction
    if (page > 1)              params.page      = page
    Object.entries(overrides).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "") params[k] = v as string | number
      else delete params[k]
    })
    setSelectedIds(new Set())
    router.get("/admin/outlet-verification", params as Record<string, string>, {
      preserveScroll: false,
    })
  }

  function handleSortColumn(col: string) {
    if (sort === col) {
      navigate({ direction: direction === "asc" ? "desc" : "asc", page: null })
    } else {
      navigate({ sort: col, direction: "asc", page: null })
    }
  }

  function historyParams(): Record<string, string> {
    const params: Record<string, string> = {}
    if (filters.status)        params.status    = filters.status
    if (filters.scope)         params.scope     = filters.scope
    if (filters.period)        params.period    = filters.period
    if (sort !== "created_at") params.sort      = sort
    if (direction !== "desc")  params.direction = direction
    return params
  }

  // -------------------------------------------------------------------------
  // Run actions (Inertia router — controller redirects)
  // -------------------------------------------------------------------------

  const inFlightBlocked = !can_create || liveInFlight.length > 0

  function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!period || creating || inFlightBlocked) return
    setCreating(true)
    router.post(
      "/admin/outlet-verification",
      // run_period/run_scope are the form fields; period/scope (inside
      // historyParams) stay reserved for the history filters.
      { run_period: period, run_scope: scopeChoice, ...historyParams() },
      { onFinish: () => setCreating(false) },
    )
  }

  function handleCancelRun(id: number) {
    router.patch(`/admin/outlet-verification/${id}/cancel`, historyParams())
  }

  function handleDeleteRun(run: RunRow) {
    const results = run.outlet_count > 0 ? ` beserta ${formatCount(run.outlet_count)} hasil` : ""
    if (!window.confirm(`Hapus run verifikasi ${run.period_label}${results}?`)) return
    router.delete(`/admin/outlet-verification/${run.id}`, { data: historyParams() })
  }

  function handleBulkDelete() {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return
    if (!window.confirm(`Hapus ${ids.length} run terpilih beserta hasilnya?`)) return
    router.delete("/admin/outlet-verification/bulk_destroy", {
      data: { ids, ...historyParams() },
      onFinish: () => setSelectedIds(new Set()),
    })
  }

  // -------------------------------------------------------------------------
  // Bulk selection
  // -------------------------------------------------------------------------

  const deletableRuns = React.useMemo(
    () => liveRuns.filter((r) => r.status !== "pending" && r.status !== "processing"),
    [liveRuns]
  )

  function toggleSelect(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectAll() {
    if (selectedIds.size === deletableRuns.length && deletableRuns.length > 0) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(deletableRuns.map((r) => r.id)))
    }
  }

  // -------------------------------------------------------------------------
  // Computed
  // -------------------------------------------------------------------------

  const hasActiveFilter = !!filters.status || !!filters.scope || !!filters.period

  const { filterOpen, setFilterOpen, sortOpen, setSortOpen, activeFilterCount, applyFilters, resetFilters } =
    useMobileFilterSort(filters, navigate, ["status", "scope", "period"] as const)
  const sortLabel =
    SORT_OPTIONS.find((o) => o.sort === sort && o.direction === direction)?.label ?? "Urutkan"
  const totalPages = Math.ceil(total / per_page)

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <>
      <Head title="Geo Verification">
        <meta
          name="description"
          content="Verifikasi koordinat dan keberadaan outlet Master Outlet — pemeriksaan internal Layer 0 per periode upload, hasil sebagai overlay read-only."
        />
        <meta property="og:title" content="Geo Verification" />
        <meta
          property="og:description"
          content="Verifikasi koordinat dan keberadaan outlet Master Outlet — pemeriksaan internal Layer 0 per periode upload, hasil sebagai overlay read-only."
        />
      </Head>
      <AppShell width="wide">
        {/* Header */}
        <div className="border-b border-hairline pb-6">
          <div className="flex items-start gap-3">
            <MapPinCheck className="mt-1 h-6 w-6 shrink-0 text-ink-muted" />
            <div>
              <h1>Geo Verification</h1>
              <p className="mt-1">
                Verifikasi koordinat &amp; keberadaan outlet dari Master Outlet: sanity koordinat,
                grup koordinat duplikat, cross-check master dist &amp; transaksi (Layer 0). Master
                data tidak pernah diubah — hasil tersimpan sebagai overlay per run.
              </p>
            </div>
          </div>
        </div>

        {/* Create-run panel */}
        <div className="mt-6 rounded-md border border-hairline bg-surface p-4 sm:p-6">
          <h2>Mulai Verifikasi</h2>

          <form onSubmit={handleCreate} className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-end">
            <div className="w-full sm:w-64">
              <label htmlFor="run-period">Periode upload</label>
              <Select
                id="run-period"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                disabled={creating || period_options.length === 0}
                className="mt-1"
              >
                {period_options.length === 0 && <option value="">Belum ada upload completed</option>}
                {period_options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                    {opt.outlet_count != null ? ` — ${formatCount(opt.outlet_count)} outlet` : ""}
                  </option>
                ))}
              </Select>
              {errors?.run_period && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.run_period}</p>
              )}
            </div>

            <div className="w-full sm:w-80">
              <label htmlFor="run-scope">Cakupan</label>
              <Select
                id="run-scope"
                value={scopeChoice}
                onChange={(e) => setScopeChoice(e.target.value as RunScope)}
                disabled={creating}
                className="mt-1"
              >
                <option value="layer0">Layer 0 — pemeriksaan internal (semua outlet)</option>
                <option value="layer0_pilot">Layer 0 + Pilot Google Maps (subset pareto)</option>
              </Select>
              {errors?.run_scope && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.run_scope}</p>
              )}
            </div>

            <Button
              type="submit"
              variant="primary"
              disabled={creating || inFlightBlocked || !period}
              className="shrink-0"
            >
              {creating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Memulai…
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Mulai Run
                </>
              )}
            </Button>
          </form>

          {scopeChoice === "layer0_pilot" &&
            (layer1_available ? (
              <p className="mt-2 text-xs text-ink-muted">
                Pilot Google Maps (Layer 1) berjalan setelah Layer 0 pada subset pareto
                (top_paretto = YES) — memakai kuota Google Maps API sesuai budget run.
              </p>
            ) : (
              <p className="mt-2 text-xs text-ink-muted">
                Layer 1 belum aktif (kill-switch OUTLET_GEO_VERIFICATION off atau API key belum
                dikonfigurasi) — run cakupan ini selesai dengan hasil Layer 0 saja plus catatan.
              </p>
            ))}
          {inFlightBlocked && (
            <p className="mt-2 text-xs text-ink-muted">
              Masih ada run yang berjalan — satu run pada satu waktu. Tunggu selesai atau batalkan
              dulu.
            </p>
          )}
          {errors?.base && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.base}</p>
          )}
        </div>

        {/* In-flight panel — always above history, sourced off the FULL active
            set (all admins' pending/processing runs) and updated live. */}
        {liveInFlight.length > 0 && (
          <div className="mt-8">
            <div className="mb-3 flex items-center gap-2">
              <h2 className="mb-0">Sedang Diproses</h2>
              <Badge tone="accent">{liveInFlight.length} aktif</Badge>
            </div>
            <div className="space-y-2">
              {liveInFlight.map((r) => {
                const progress = progressMap[r.id] ?? { processed: r.processed_count, total: r.outlet_count }
                const pct = progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0
                return (
                  <div key={r.id} className="rounded-md border border-hairline bg-surface px-4 py-3">
                    <div className="flex flex-wrap items-center gap-3">
                      <StatusBadge status={r.status} />
                      <span className="font-medium text-ink-display">{r.period_label}</span>
                      <ScopeBadge scope={r.scope} />
                      {r.triggered_by && (
                        <span className="text-xs text-ink-muted">oleh {r.triggered_by}</span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-auto gap-1 text-red-600"
                        onClick={() => handleCancelRun(r.id)}
                      >
                        <Ban className="h-3.5 w-3.5" />
                        Batalkan
                      </Button>
                    </div>
                    {r.status === "processing" && (
                      <div className="mt-2 space-y-1">
                        <ProgressBar value={pct} />
                        <p className="text-xs text-ink-muted">
                          {formatCount(progress.processed)} / {formatCount(progress.total)}{" "}
                          {progress.phase === "layer1" ? "outlet pilot (Layer 1)" : "outlet"}
                          {progress.total > 0 ? ` (${pct}%)` : ""}
                        </p>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* History */}
        <div className="mt-10">
          <h2 className="mb-4">Riwayat Verifikasi</h2>

          {/* Filter + Sort bar (mobile) */}
          <div className="mb-4 md:hidden">
            <MobileFilterSortBar
              filterCount={activeFilterCount}
              sortLabel={sortLabel}
              onFilterClick={() => setFilterOpen(true)}
              onSortClick={() => setSortOpen(true)}
            />
          </div>

          {/* Filter bar (desktop) */}
          <div className="mb-4 hidden flex-wrap items-end gap-3 md:flex">
            <Select
              value={filters.status ?? ""}
              onChange={(e) => navigate({ status: e.target.value || null, page: null })}
              className="w-36"
            >
              <option value="">Semua Status</option>
              {filter_options.statuses.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>

            <Select
              value={filters.scope ?? ""}
              onChange={(e) => navigate({ scope: e.target.value || null, page: null })}
              className="w-44"
            >
              <option value="">Semua Cakupan</option>
              {filter_options.scopes.map((s) => (
                <option key={s} value={s}>{scopeLabel(s)}</option>
              ))}
            </Select>

            <Select
              value={filters.period ?? ""}
              onChange={(e) => navigate({ period: e.target.value || null, page: null })}
              className="w-40"
            >
              <option value="">Semua Periode</option>
              {filter_options.periods.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </Select>

            {hasActiveFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate({ status: null, scope: null, period: null, page: null })}
              >
                Reset filter
              </Button>
            )}
          </div>

          {liveRuns.length === 0 ? (
            <p className="text-sm text-ink-muted">
              {hasActiveFilter ? "Tidak ada run yang cocok dengan filter." : "Belum ada run verifikasi."}
            </p>
          ) : (
            <>
              {/* Bulk action bar */}
              {selectedIds.size > 0 && (
                <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-hairline bg-surface px-3 py-2">
                  <span className="text-xs text-ink-muted">{selectedIds.size} terpilih</span>
                  <Button variant="ghost" size="sm" type="button" onClick={handleBulkDelete}>
                    <Trash2 className="mr-1 h-3 w-3" />
                    Hapus terpilih
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    className="ml-auto"
                    onClick={() => setSelectedIds(new Set())}
                  >
                    Batal pilih
                  </Button>
                </div>
              )}

              {/* Mobile card list */}
              <div className="space-y-3 md:hidden">
                {liveRuns.map((r) => (
                  <RunCard
                    key={r.id}
                    run={r}
                    selected={selectedIds.has(r.id)}
                    onToggleSelect={() => toggleSelect(r.id)}
                    onCancel={() => handleCancelRun(r.id)}
                    onDelete={() => handleDeleteRun(r)}
                  />
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden overflow-hidden rounded-md border border-hairline md:block">
                <table className="w-full text-sm">
                  <thead className="bg-surface">
                    <tr>
                      <th className="w-10 px-3 py-2.5">
                        <Checkbox
                          checked={
                            deletableRuns.length > 0 &&
                            selectedIds.size === deletableRuns.length
                          }
                          onChange={toggleSelectAll}
                          aria-label="Pilih semua"
                          disabled={deletableRuns.length === 0}
                        />
                      </th>
                      <SortableHeader
                        col="period" label="Periode"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <th className="px-4 py-2.5 text-left font-medium text-ink-muted">Cakupan</th>
                      <SortableHeader
                        col="status" label="Status"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <SortableHeader
                        col="outlet_count" label="Outlet" align="right"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <SortableHeader
                        col="layer0_flagged_count" label="Bermasalah" align="right"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <th className="px-4 py-2.5 text-left font-medium text-ink-muted">Oleh</th>
                      <SortableHeader
                        col="created_at" label="Waktu"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <th className="w-10 px-2 py-2.5" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-hairline">
                    {liveRuns.map((r) => (
                      <RunTableRow
                        key={r.id}
                        run={r}
                        selected={selectedIds.has(r.id)}
                        onToggleSelect={() => toggleSelect(r.id)}
                        onCancel={() => handleCancelRun(r.id)}
                        onDelete={() => handleDeleteRun(r)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Summary + pagination */}
          {total > 0 && (
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-ink-muted">
                Menampilkan {(page - 1) * per_page + 1}–{Math.min(page * per_page, total)} dari{" "}
                {total} run
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => navigate({ page: page - 1 })}
                  >
                    Sebelumnya
                  </Button>
                  <span className="text-sm text-ink-muted">
                    Hal. {page} / {totalPages}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => navigate({ page: page + 1 })}
                  >
                    Berikutnya
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile filter sheet */}
        <MobileFilterSheet
          open={filterOpen}
          onOpenChange={setFilterOpen}
          initial={{
            status: filters.status ?? "",
            scope: filters.scope ?? "",
            period: filters.period ?? "",
          }}
          onApply={applyFilters}
          onReset={resetFilters}
        >
          {(draft, setDraft) => (
            <>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-ink-display">Status</span>
                <Select
                  value={draft.status}
                  onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                >
                  <option value="">Semua Status</option>
                  {filter_options.statuses.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </Select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-ink-display">Cakupan</span>
                <Select
                  value={draft.scope}
                  onChange={(e) => setDraft({ ...draft, scope: e.target.value })}
                >
                  <option value="">Semua Cakupan</option>
                  {filter_options.scopes.map((s) => (
                    <option key={s} value={s}>{scopeLabel(s)}</option>
                  ))}
                </Select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-ink-display">Periode</span>
                <Select
                  value={draft.period}
                  onChange={(e) => setDraft({ ...draft, period: e.target.value })}
                >
                  <option value="">Semua Periode</option>
                  {filter_options.periods.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </Select>
              </label>
            </>
          )}
        </MobileFilterSheet>

        {/* Mobile sort sheet */}
        <MobileSortSheet
          open={sortOpen}
          onOpenChange={setSortOpen}
          current={{ sort, direction }}
          options={SORT_OPTIONS}
          onSelect={(opt) => {
            navigate({ sort: opt.sort, direction: opt.direction })
            setSortOpen(false)
          }}
        />
      </AppShell>
    </>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SortableHeader({
  col,
  label,
  sort,
  direction,
  onSort,
  align = "left",
}: {
  col: string
  label: string
  sort: string
  direction: "asc" | "desc"
  onSort: (col: string) => void
  align?: "left" | "right"
}) {
  const active = sort === col
  return (
    <th
      className={`cursor-pointer select-none px-4 py-2.5 font-medium text-ink-muted hover:text-ink-body text-${align}`}
      onClick={() => onSort(col)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (
          direction === "asc" ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </span>
    </th>
  )
}

function RunTableRow({
  run,
  selected,
  onToggleSelect,
  onCancel,
  onDelete,
}: {
  run: RunRow
  selected: boolean
  onToggleSelect: () => void
  onCancel: () => void
  onDelete: () => void
}) {
  const isInFlight = run.status === "pending" || run.status === "processing"

  return (
    <tr>
      <td className="px-3 py-3">
        <Checkbox
          checked={selected}
          onChange={onToggleSelect}
          disabled={isInFlight}
          aria-label={`Pilih run ${run.period_label}`}
        />
      </td>
      <td className="px-4 py-3 font-medium text-ink-display">
        <Link
          href={`/admin/outlet-verification/${run.id}`}
          className="hover:text-accent hover:underline"
        >
          {run.period_label}
        </Link>
      </td>
      <td className="px-4 py-3">
        <ScopeBadge scope={run.scope} />
        {run.layer1_note && (
          <p className="mt-1 max-w-[220px] text-xs text-ink-muted" title={run.layer1_note}>
            Layer 1 menyusul di M2
          </p>
        )}
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={run.status} />
        {run.error_message && (
          <p className="mt-1 max-w-[240px] truncate text-xs text-red-600 dark:text-red-400" title={run.error_message}>
            {run.error_message}
          </p>
        )}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-ink-body">
        {formatCount(run.outlet_count)}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-ink-body">
        {run.status === "completed" ? (
          <>
            {formatCount(run.layer0_flagged_count)}
            {run.outlet_count > 0 && (
              <span className="ml-1 text-xs text-ink-muted">
                ({Math.round((run.layer0_flagged_count / run.outlet_count) * 100)}%)
              </span>
            )}
          </>
        ) : (
          "—"
        )}
      </td>
      <td className="px-4 py-3 text-xs text-ink-muted">{run.triggered_by ?? "—"}</td>
      <td className="px-4 py-3 text-xs text-ink-muted">{formatDate(run.created_at)}</td>
      <td className="px-2 py-3">
        <div className="flex items-center justify-end gap-1">
          <Link
            href={`/admin/outlet-verification/${run.id}`}
            title="Lihat detail run"
            className="rounded p-1 text-ink-muted transition-colors hover:text-accent"
          >
            <Eye className="h-3.5 w-3.5" />
          </Link>
          {isInFlight ? (
            <button
              onClick={onCancel}
              title="Batalkan run ini"
              className="rounded p-1 text-ink-muted transition-colors hover:text-red-600"
            >
              <Ban className="h-3.5 w-3.5" />
            </button>
          ) : (
            <button
              onClick={onDelete}
              title="Hapus run ini"
              className="rounded p-1 text-ink-muted transition-colors hover:text-red-600"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}

function RunCard({
  run,
  selected,
  onToggleSelect,
  onCancel,
  onDelete,
}: {
  run: RunRow
  selected: boolean
  onToggleSelect: () => void
  onCancel: () => void
  onDelete: () => void
}) {
  const isInFlight = run.status === "pending" || run.status === "processing"

  return (
    <DataCard>
      <DataCardHeader>
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <Checkbox
            checked={selected}
            onChange={onToggleSelect}
            disabled={isInFlight}
            aria-label={`Pilih run ${run.period_label}`}
            className="mt-0.5 shrink-0"
          />
          <DataCardTitle>{run.period_label}</DataCardTitle>
        </div>
        <DataCardStatus><StatusBadge status={run.status} /></DataCardStatus>
      </DataCardHeader>
      <DataCardGrid>
        <DataCardField label="Cakupan" value={scopeLabel(run.scope)} />
        <DataCardField label="Outlet" value={formatCount(run.outlet_count)} />
        <DataCardField
          label="Bermasalah"
          value={run.status === "completed" ? formatCount(run.layer0_flagged_count) : "—"}
        />
        <DataCardField label="Oleh" value={run.triggered_by ?? "—"} />
        <DataCardField wide label="Waktu" value={formatDate(run.created_at)} />
        {run.error_message && (
          <DataCardField wide label="Error" value={run.error_message} />
        )}
        {run.layer1_note && (
          <DataCardField wide label="Layer 1" value={run.layer1_note} />
        )}
      </DataCardGrid>
      <DataCardActions>
        <Button asChild variant="ghost" size="sm" className="gap-2">
          <Link href={`/admin/outlet-verification/${run.id}`}>
            <Eye className="h-4 w-4" />
            Detail
          </Link>
        </Button>
        {isInFlight ? (
          <Button variant="ghost" size="sm" onClick={onCancel} className="gap-2 text-red-600">
            <Ban className="h-4 w-4" />
            Batalkan
          </Button>
        ) : (
          <Button variant="ghost" size="sm" onClick={onDelete} className="gap-2 text-red-600">
            <Trash2 className="h-4 w-4" />
            Hapus
          </Button>
        )}
      </DataCardActions>
    </DataCard>
  )
}

function ScopeBadge({ scope }: { scope: RunScope }) {
  return scope === "layer0_pilot" ? (
    <Badge tone="accent">L0 + Pilot</Badge>
  ) : (
    <Badge tone="muted">Layer 0</Badge>
  )
}

function StatusBadge({ status }: { status: RunStatus }) {
  switch (status) {
    case "pending":
      return (
        <Badge tone="muted">
          <Clock className="mr-1 h-3 w-3" />
          Pending
        </Badge>
      )
    case "processing":
      return (
        <Badge tone="accent">
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          Processing
        </Badge>
      )
    case "completed":
      return (
        <Badge tone="success">
          <CheckCircle2 className="mr-1 h-3 w-3" />
          Completed
        </Badge>
      )
    case "failed":
      return (
        <Badge tone="danger">
          <XCircle className="mr-1 h-3 w-3" />
          Failed
        </Badge>
      )
    case "cancelled":
      return (
        <Badge tone="muted">
          <Ban className="mr-1 h-3 w-3" />
          Dibatalkan
        </Badge>
      )
  }
}

function ProgressBar({ value }: { value?: number }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-hairline">
      <div
        className="h-full rounded-full bg-accent transition-all duration-300"
        style={{ width: `${Math.min(value ?? 0, 100)}%` }}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function scopeLabel(scope: string): string {
  return scope === "layer0_pilot" ? "Layer 0 + Pilot" : "Layer 0"
}

function formatCount(n: number): string {
  return n.toLocaleString("id-ID")
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("id-ID", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}
